import useLayoutStore, {
  LayoutMode,
  LeftTab,
} from "@/store/zustand/layoutStore";
import { useStartServer, useRunTerminalCommand } from "@/utils/devServerUtils";
import { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { Button } from "../ui/button";
import { getAxiosErorrMessage } from "@/utils/utils";
import { toast } from "sonner";

export default function Convex() {
  const [syncing, setSyncing] = useState(false);
  const [interactiveSyncing, setInteractiveSyncing] = useState(false);
  const [terminalOutput, setTerminalOutput] = useState<string[]>([]);
  const startServer = useStartServer();
  const runTerminalCommand = useRunTerminalCommand();
  const setNewLayout = useLayoutStore((state) => state.setNewLayout);

  const openConvexAndTerminal = () => {
    // Switch to code mode and open Convex tab
    setNewLayout(LeftTab.Convex, LayoutMode.Code);
  };

  const handleInteractiveCommand = async () => {
    setInteractiveSyncing(true);
    setTerminalOutput([]);
    try {
      // Open Convex tab and terminal
      openConvexAndTerminal();

      const { success, error } = await runTerminalCommand({
        command: `npx convex login --device-name ${uuidv4()} --no-open`,
      });

      if (!success) {
        throw new Error(error || "Unknown error");
      }

      setTerminalOutput((prev) => [
        ...prev,
        "Successfully connected to Convex dev server",
      ]);

      // Wait longer to ensure the Convex auth URL is detected and opened
      setTimeout(() => {
        startServer({
          forceRestart: true,
        });
      }, 20000); // Increased from 5000 to 10000ms to ensure auth URL is processed
    } catch (error) {
      toast.error("Error connecting to Convex: " + getAxiosErorrMessage(error));
      setTerminalOutput((prev) => [
        ...prev,
        "Failed to connect to Convex dev server",
      ]);
    } finally {
      setInteractiveSyncing(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      // Open Convex tab and terminal
      openConvexAndTerminal();

      const { success, error } = await runTerminalCommand({
        command: `npx convex dev`,
      });

      if (!success) {
        throw new Error(error || "Unknown error");
      }

      setSyncing(false);
    } catch (error) {
      console.error("Error running sync command:", error);
      toast.error("Error running sync command: " + getAxiosErorrMessage(error));
      setSyncing(false);
    }
  };

  const ConvexLogoIcon = (props: any) => (
    <img src="/images/convex.png" alt="Convex" {...props} />
  );

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-col gap-2 text-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          <h3 className="font-medium text-foreground">Convex Integration</h3>
          <span className="px-2 py-0.5 text-xs bg-blue-500/10 text-blue-500 rounded-full">
            Beta
          </span>
        </div>
        <p>
          Connect and sync your Convex backend to enable real-time collaboration
          and data persistence. First connect to authenticate, then sync to
          start the development server.
        </p>
      </div>
      <div className="flex flex-row justify-center items-center gap-2">
        <Button
          className="w-full bg-background-secondary hover:bg-background-tertiary gap-1"
          variant="outline"
          onClick={handleInteractiveCommand}
          disabled={interactiveSyncing}
        >
          <ConvexLogoIcon className="w-3 h-3" />
          {interactiveSyncing ? "Connecting..." : "Connect"}
        </Button>
        <Button
          className="w-full gap-1"
          variant="primary"
          onClick={handleSync}
          disabled={syncing}
        >
          <ConvexLogoIcon className="w-3 h-3" />
          {syncing ? "Syncing..." : "Sync"}
        </Button>
      </div>
      {terminalOutput.length > 0 && (
        <div className="mt-4 bg-gray-900 text-gray-200 p-4 rounded-md font-mono text-sm overflow-y-auto max-h-[400px] w-full">
          {terminalOutput.map((line, index) => (
            <div key={index} className="whitespace-pre-wrap">
              {line}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
